import type { QueryClient } from '@tanstack/react-query';

import { adminKeys } from '@/api/admin-keys';
import { queryKeys } from '@/api/query-keys';
import { apiRoutes } from '@/api/routes';
import { notePersonWrite } from '@/api/write-clock';

/**
 * What each write changes, declared once, beside the route rather than at the screen that calls it.
 *
 * **Freshness used to be hand-written at sixteen call sites, and it was demonstrably incomplete.**
 * Acknowledging an alert refreshed the alert list but not the figure rendered directly above it; a
 * movement recorded at the gate invalidated nothing at all, so the occupancy card, the roll-call and
 * that person's own record all went stale; adding a photograph refreshed the photographs and left
 * the "record incomplete" banner beside them saying a photograph was missing. Each of those is one
 * forgotten line at one call site, and a reader looking at the screen cannot tell a deliberate
 * omission from an oversight. Declared here, the omission has to be written down as `[]`.
 *
 * **Totality is over the table's own keys, not over route ids.** `RouteSpec.id` and `method` are
 * widened to `string` and `HttpMethod` (`api/routes.ts`), and `route()` infers neither as a literal,
 * so there is no route-id union to be total over — but `apiRoutes`' own keys are literals, and
 * `Record<keyof typeof apiRoutes, …>` therefore does not compile until every route has an answer.
 * `null` is the answer for a read. What the compiler still cannot say is that a non-`GET` route got
 * a real one and a `GET` route did not; `__tests__/invalidation.web.test.ts` is the census that
 * does, and it fails in both directions.
 *
 * **And totality does not mean the effects were applied.** Roughly a dozen mutating routes never
 * reach {@link useApiMutation} — the gate submits a movement through the client directly, so does
 * the record screen, so does the field reorder, and so does the photograph attach that follows a
 * registration. Those call {@link applyWriteEffects} themselves. A table cannot check that, so the
 * same census walks the production sources for a direct `client.request` on a non-`GET` route and
 * requires each one to be on a reviewed list.
 *
 * **Nothing here costs the gate a request.** `invalidateQueries` defaults to `refetchType: 'active'`,
 * so an effect issues a request only where that view is mounted. A movement at a door declares that
 * it changes the occupancy, the roll-call, the outside list, the Live panel and that person's
 * record — and at the gate, where none of those is on screen, every one of them is a cache mark and
 * zero traffic. The same declaration refreshes an administrator's dashboard while they are looking
 * at it. That is what makes a complete table safe on the busiest path in the product, and it is why
 * effects are DECLARED rather than issued.
 */

/** What the write was, for the effects that need to name one record rather than a whole list. */
export interface WriteContext {
  /** The path parameters the call filled, so an effect can name the record it changed. */
  readonly params: Readonly<Record<string, string>>;
  /** The request body, for the routes that carry the subject there rather than in the path. */
  readonly body: unknown;
  /** What the server answered, for the guards that have to tell a write from a 2xx that was not. */
  readonly result: unknown;
}

/**
 * A cache key this write makes stale.
 *
 * A plain array for the many effects that name a whole list, and a function only for the few that
 * have to name one record — which is most of the table written as data rather than as a closure per
 * row, and a smaller eager bundle for the same declaration.
 */
type KeyFor = readonly unknown[] | ((context: WriteContext) => readonly unknown[]);

/**
 * One consequence of a write.
 *
 * `refetch: 'none'` marks a query stale WITHOUT asking for it now, and it is not a weaker version of
 * the default — it is the right answer twice over. The register is fed by a projection a second or
 * two behind, so re-asking the instant a person is written answers with the old page; what re-asks
 * is `api/write-clock.ts`'s one-second window, and the stale mark is what closes the gap after that
 * window is spent but inside the fifteen-second `staleTime`. And `person.get` writes the
 * `person.view_full` row AD-37's exfiltration threshold counts, so forcing a mounted record to
 * re-read is a counted audit row the operator did not ask for; marking it stale spends nothing now
 * and costs exactly one row the next time they open the record, which is a read they are making
 * anyway.
 */
type WriteEffect =
  | { readonly invalidate: KeyFor; readonly refetch?: 'active' | 'none' }
  /**
   * Removes the cached answers under a key outright, rather than marking them stale.
   *
   * For a photograph that no longer exists or a person who was erased: a stale mark would keep the
   * decrypted face in memory until the cache let go of it, and the honest state of that memory
   * after an erasure is empty. A view still drawing one asks again and is told it is missing.
   */
  | { readonly evict: KeyFor }
  /** Opens `api/write-clock.ts`'s catch-up window, for a write the search projection has to see. */
  | { readonly projectionCatchUp: true };

/** A route's effects, with an optional guard for the answers that did not actually write. */
type WriteEffects =
  | readonly WriteEffect[]
  | {
      readonly effects: readonly WriteEffect[];
      /** Whether THIS 2xx was a write. Absent means every success was. */
      readonly appliesTo: (result: unknown) => boolean;
    };

const person = (context: WriteContext): string => context.params.id ?? '';

/** The subject of a presence submission, which travels in the body rather than in the path. */
function submittedPersonId(context: WriteContext): string {
  const body = context.body;
  if (typeof body !== 'object' || body === null) {
    return '';
  }
  const value = (body as { readonly personId?: unknown }).personId;
  return typeof value === 'string' ? value : '';
}

/** Every view a movement changes. Written once, because four routes move somebody. */
const PRESENCE_VIEWS: readonly WriteEffect[] = [
  { invalidate: queryKeys.presenceCounts() },
  { invalidate: adminKeys.rollCall() },
  { invalidate: adminKeys.outsideNow() },
  { invalidate: adminKeys.panelPrefix('live') },
];

/**
 * Every view a serving changes.
 *
 * **`diningSubmit` is not `null`, and that is the point of writing this out.** A meal moves exactly
 * the figures an administrator may be watching while an operator serves it: the roster they are
 * reading to find who has had nothing, and the panel beside it. Neither is on screen at the counter,
 * so at the counter this is a cache mark and zero requests — `invalidateQueries` refetches only
 * ACTIVE observers. What it buys is a dashboard that is correct while somebody is looking at it,
 * which is exactly what `PRESENCE_VIEWS` buys for a movement.
 */
const DINING_VIEWS: readonly WriteEffect[] = [
  { invalidate: adminKeys.diningRosterPrefix() },
  { invalidate: adminKeys.panelPrefix('dining') },
];

/** The two views a change to the register's own population reaches. */
const REGISTER_VIEWS: readonly WriteEffect[] = [
  { projectionCatchUp: true },
  { invalidate: queryKeys.personRegisterPrefix(), refetch: 'none' },
];

/** The pass-stock picture, which three different screens show a part of. */
const PASS_STOCK: readonly WriteEffect[] = [
  { invalidate: adminKeys.batches() },
  { invalidate: adminKeys.panelPrefix('registry') },
  // `blanks_low` is raised and cleared by the server on each claim, import and void, so the open
  // alert set is a different answer after any of them.
  { invalidate: adminKeys.alertsPrefix() },
];

export const WRITE_EFFECTS: Record<keyof typeof apiRoutes, WriteEffects | null> = {
  // ---- reads -------------------------------------------------------------------------------
  systemConfig: null,
  authCsrf: null,
  authSession: null,
  personList: null,
  fieldList: null,
  personGet: null,
  personImageList: null,
  personThumbnails: null,
  credentialList: null,
  personRevisions: null,
  userList: null,
  settingList: null,
  apiKeyList: null,
  apiKeyUsage: null,
  auditList: null,
  auditChain: null,
  auditActions: null,
  exportList: null,
  exportGet: null,
  backupList: null,
  maintenanceStatus: null,
  reconciliationCandidates: null,
  rollCall: null,
  outsideNow: null,
  presenceCounts: null,
  alertList: null,
  incidentList: null,
  analyticsLive: null,
  analyticsTraffic: null,
  analyticsRegistry: null,
  analyticsIntegrity: null,
  analyticsOperators: null,
  analyticsSecurity: null,
  analyticsApi: null,
  analyticsSystem: null,
  credentialBatchList: null,
  credentialBatchGet: null,

  // ---- authentication ----------------------------------------------------------------------
  // **Every one of these is `[]`, and the empty list is the statement.** What a sign-in, a
  // hand-over, a sign-out or an unlock does to this cache is decided by WHO is now at the terminal,
  // which is `app/providers/session-cache.tsx`'s question and not a per-route one: an account
  // change resets everything, and a return from the lock resets exactly what the lock refused.
  // Naming a key here would be a second, quieter answer to the same question.
  authLogin: [],
  authLogout: [],
  authSwitchOperator: [],
  authChangePassword: [],
  authSetPin: [],
  authPinUnlock: [],
  authPasswordUnlock: [],
  authTotpBegin: [],
  authTotpConfirm: [],
  // Section 1.7's reset. `[]` like every other entry here, and for a stronger reason: these three
  // are ANONYMOUS, so at the moment they run there is nothing in this cache belonging to anybody.
  // Not `null` either — they are writes, and the census refuses a non-GET holding one.
  authResetRequest: [],
  authResetPassword: [],
  authResetTotp: [],

  // ---- the register ------------------------------------------------------------------------
  personCreate: {
    effects: REGISTER_VIEWS,
    // **A 2xx is not always a write.** `confirmation_required` is the completeness dialog: the
    // record does not exist, the form re-submits with `acceptIncomplete`, and treating that answer
    // as a registration would open a catch-up poll and mark the register stale for somebody who was
    // never created — every time an operator is asked to confirm an incomplete record.
    appliesTo: (result) =>
      typeof result !== 'object' ||
      result === null ||
      (result as { readonly kind?: unknown }).kind !== 'confirmation_required',
  },
  personPatch: [
    ...REGISTER_VIEWS,
    // The record itself is SEEDED from the response by the screen that wrote it, which is better
    // than invalidating: it costs no request and no `person.view_full` row. What the response does
    // not carry is the revision list, so that is asked again.
    { invalidate: (context) => queryKeys.personRevisions(person(context)) },
  ],
  // A read wearing a POST, because the fields it asks about are personal data and belong in a body.
  personDuplicateCandidates: [],
  personImageUpload: [
    { invalidate: (context) => queryKeys.personImages(person(context)) },
    // The completeness warning is computed on the RECORD, not on the photograph list, so without
    // this a record kept saying a photograph was missing beside the photograph that had just been
    // added. Stale rather than refetched: see the note on `refetch` above.
    { invalidate: (context) => queryKeys.personDetail(person(context)), refetch: 'none' },
    ...REGISTER_VIEWS,
  ],
  personImageCapture: [
    { invalidate: (context) => queryKeys.personImages(person(context)) },
    { invalidate: (context) => queryKeys.personDetail(person(context)), refetch: 'none' },
    ...REGISTER_VIEWS,
  ],
  personImageRemove: [
    { invalidate: (context) => queryKeys.personImages(person(context)) },
    { invalidate: (context) => queryKeys.personDetail(person(context)), refetch: 'none' },
    // The removed photograph's thumbnail, in both retention classes: the bytes are gone from the
    // store, and so must the copy this browser decrypted be.
    { evict: (context) => queryKeys.personThumbnail(context.params.imageId ?? '') },
    { evict: (context) => queryKeys.gateThumbnail(context.params.imageId ?? '') },
    ...REGISTER_VIEWS,
  ],
  // The import's two reads wearing a POST: the rows are personal data and travel in a body, and
  // neither keeps nor writes anything.
  personImportWorkbook: [],
  personImportAnalysis: [],
  // **Every person an import creates is INSIDE from the moment the row commits** (R351), so a chunk
  // moves the population and every presence figure at once, exactly as an erasure does in reverse.
  personImportCommit: {
    effects: [...REGISTER_VIEWS, ...PRESENCE_VIEWS],
    // A chunk whose every row was refused created nobody, and opening the catch-up window for it
    // would set the register polling for people who do not exist.
    appliesTo: (result) =>
      (
        result as { readonly rows?: readonly { readonly outcome: string }[] } | undefined
      )?.rows?.some((row) => row.outcome === 'created') !== false,
  },
  // An erasure removes somebody from the population and from every presence figure. Dropping their
  // decrypted record from the cache is the CALLER's, because it has to happen after the navigation:
  // removing a query something is still observing makes the library fetch it again, which would be
  // a read of a person who has just been erased.
  //
  // **Every cached thumbnail is evicted**, in both retention classes. The erased person's image ids
  // are not in the request, and the photographs of everybody else are one batch away from being
  // drawn again, so the whole class goes rather than leaving a face that was just erased in memory.
  personShred: [...REGISTER_VIEWS, ...PRESENCE_VIEWS, { evict: queryKeys.personThumbnailPrefix() }],

  // ---- the gate ----------------------------------------------------------------------------
  // The resolve writes a `scan_attempts` row and mints a key; it changes nothing this browser reads.
  presenceResolve: [],
  presenceSubmit: [
    ...PRESENCE_VIEWS,
    // **Refetched rather than marked stale, and this is the one `person.get` effect that is.** A
    // movement changes the presence state the RECORD SCREEN is displaying, so a mark would leave
    // the operator looking at "inside" for somebody they have just recorded leaving. It costs one
    // `person.view_full` row, and only where that record is actually on screen — at a gate, where
    // no record is mounted, `refetchType: 'active'` makes it free. The row is honest: the operator
    // is watching that record change.
    { invalidate: (context) => queryKeys.personDetail(submittedPersonId(context)) },
    // Every write that changes a record writes a `person_revisions` row, and the history panel sits
    // on the same screen.
    { invalidate: (context) => queryKeys.personRevisions(submittedPersonId(context)) },
  ],

  // ---- the counter -------------------------------------------------------------------------
  // The roster and the panel are reads and take `null`, exactly as the roll-call and the Live panel
  // above do.
  diningRoster: null,
  analyticsDining: null,
  diningSubmit: [
    ...DINING_VIEWS,
    // **A serving moves the activity clock, and the record screen shows it.** `persons.last_activity_at`
    // is what the retention report reads, and it is on the record. Marked stale rather than
    // refetched, unlike the movement above: a meal does not change the presence state somebody is
    // looking at, so there is nothing on screen that would be WRONG until the next read, and a
    // refetch would spend a `person.view_full` row per meal on a path that serves a queue.
    {
      invalidate: (context) => queryKeys.personDetail(submittedPersonId(context)),
      refetch: 'none',
    },
  ],

  // ---- passes ------------------------------------------------------------------------------
  // Both write an audit row and neither changes a view: what they answer is shown in place and
  // nothing lists it afterwards.
  credentialReveal: [],
  credentialPrint: [],
  credentialLookup: [],
  // A reissue supersedes the existing pass, and in a pre-printed installation it CLAIMS a blank —
  // so the box it came out of has one fewer available and the stock panel is a different answer.
  credentialIssue: [{ invalidate: queryKeys.personCredentialsPrefix() }, ...PASS_STOCK],
  credentialRevoke: [{ invalidate: queryKeys.personCredentialsPrefix() }],
  credentialBatchGenerate: PASS_STOCK,
  credentialBatchImport: PASS_STOCK,
  credentialBatchVoid: PASS_STOCK,
  credentialBlankVoid: PASS_STOCK,

  // ---- administration ----------------------------------------------------------------------
  userCreate: [
    { invalidate: adminKeys.users() },
    { invalidate: adminKeys.panelPrefix('operators') },
  ],
  userPatch: [
    { invalidate: adminKeys.users() },
    { invalidate: adminKeys.panelPrefix('operators') },
  ],
  userResetPassword: [{ invalidate: adminKeys.users() }],
  userResetTotp: [{ invalidate: adminKeys.users() }],
  fieldCreate: [{ invalidate: queryKeys.fieldDefinitions() }],
  fieldAmend: [{ invalidate: queryKeys.fieldDefinitions() }],
  // A purge destroys that field's value for EVERY person, so a cached record still holding it is
  // showing something the installation no longer has.
  fieldPurge: [
    { invalidate: queryKeys.fieldDefinitions() },
    { invalidate: queryKeys.personDetailPrefix(), refetch: 'none' },
    ...REGISTER_VIEWS,
  ],
  settingUpdate: [{ invalidate: adminKeys.settings() }],
  apiKeyCreate: [{ invalidate: adminKeys.apiKeys() }, { invalidate: adminKeys.panelPrefix('api') }],
  apiKeyRevoke: [{ invalidate: adminKeys.apiKeys() }, { invalidate: adminKeys.panelPrefix('api') }],
  exportCreate: [{ invalidate: adminKeys.exports() }],
  backupCreate: [{ invalidate: adminKeys.backups() }],
  backupPurge: [{ invalidate: adminKeys.backups() }],
  // Arming a restore puts the installation into maintenance, and the banner that says so is drawn
  // from this query. Before it was declared here the screen waited for its own three-second tick.
  backupRestore: [{ invalidate: adminKeys.maintenance() }],
  maintenanceSet: [{ invalidate: adminKeys.maintenance() }],
  reconcile: [{ invalidate: adminKeys.reconciliation() }, ...PRESENCE_VIEWS, ...REGISTER_VIEWS],
  // The "open alerts" figure is rendered directly above the list this changes, from a different
  // query. They disagreed for up to thirty seconds.
  alertAcknowledge: [
    { invalidate: adminKeys.alertsPrefix() },
    { invalidate: adminKeys.panelPrefix('security') },
  ],
};

/** The effects of one route, by identity rather than by a name somebody could misspell. */
const BY_ROUTE = new Map<unknown, WriteEffects | null>(
  Object.entries(apiRoutes).map(([key, route]) => [
    route,
    WRITE_EFFECTS[key as keyof typeof apiRoutes],
  ]),
);

function listOf(declared: WriteEffects, result: unknown): readonly WriteEffect[] {
  // Narrowed on the GUARD's presence rather than with `Array.isArray`, which widens a readonly
  // tuple to `any[]` and makes the return unsafe for no gain: the two shapes differ by that member.
  if ('appliesTo' in declared) {
    return declared.appliesTo(result) ? declared.effects : [];
  }
  return declared;
}

/**
 * Applies what a route declares it changed.
 *
 * **It never throws and never returns a promise the caller is expected to await**, and both are
 * load-bearing. `useApiMutation` calls this from inside `onSuccess`, which `person-form.tsx`
 * documents as re-entrant and never deferred: the library awaits that callback before it dispatches
 * success, and the registration form re-submits from inside it so that the Save button stays
 * disabled across the swap. An `await` here would put a microtask in that gap, the button would be
 * enabled for one frame, and a second press in that frame is a second person with a second active
 * pass. So the invalidations are started and left to run.
 */
export function applyWriteEffects(
  queries: QueryClient,
  route: unknown,
  context: WriteContext,
): void {
  const declared = BY_ROUTE.get(route);
  if (declared === null || declared === undefined) {
    return;
  }
  for (const effect of listOf(declared, context.result)) {
    if ('projectionCatchUp' in effect) {
      notePersonWrite();
      continue;
    }
    if ('evict' in effect) {
      queries.removeQueries({
        queryKey: typeof effect.evict === 'function' ? effect.evict(context) : effect.evict,
      });
      continue;
    }
    void queries.invalidateQueries({
      queryKey:
        typeof effect.invalidate === 'function' ? effect.invalidate(context) : effect.invalidate,
      ...(effect.refetch === undefined ? {} : { refetchType: effect.refetch }),
    });
  }
}
